import React from 'react';
import { LoadingSpinner } from '../../../components/LoadingSpinner';

type Props = {
  title: string;
  onSave: (title: string) => Promise<string | void>;
  onTitleClick?: () => void;
  /** Kompakte Listen-Darstellung (Sidebar): kleinere Schrift/Buttons. */
  compact?: boolean;
  className?: string;
  /** Damit der Aufrufer z. B. sein Zeilenmenü ausblenden kann, solange editiert wird. */
  onEditingChange?: (editing: boolean) => void;
};

export type EditableChatTitleHandle = {
  /** Der Umbenennen-Eintrag liegt im Zeilenmenü, nicht mehr in dieser Komponente. */
  startEditing: () => void;
};

/** Muss `DEFAULT_AI_CHAT_TITLE` in `server/services/ai/chatTitle.ts` entsprechen. */
const DEFAULT_TITLE = 'Neuer Chat';
const TITLE_MAX_LENGTH = 120;

export const EditableChatTitle = React.forwardRef<EditableChatTitleHandle, Props>(function EditableChatTitle({
  title,
  onSave,
  onTitleClick,
  compact = false,
  className = '',
  onEditingChange,
}: Props, ref) {
  const [editing, setEditing] = React.useState(false);
  const [draft, setDraft] = React.useState(title);
  const [saving, setSaving] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const savingRef = React.useRef(false);
  const skipNextBlurRef = React.useRef(false);

  const applyEditing = (next: boolean) => {
    setEditing(next);
    onEditingChange?.(next);
  };

  React.useEffect(() => {
    if (!editing) setDraft(title);
  }, [editing, title]);

  React.useLayoutEffect(() => {
    if (!editing) return;
    const el = inputRef.current;
    if (!el) return;
    el.focus();
    if (el.value.trim() === DEFAULT_TITLE) {
      el.select();
    } else {
      el.setSelectionRange(el.value.length, el.value.length);
    }
  }, [editing]);

  const cancel = () => {
    if (savingRef.current) return;
    skipNextBlurRef.current = true;
    setDraft(title);
    setError(null);
    applyEditing(false);
  };

  const save = async () => {
    if (savingRef.current) return;
    const next = draft.trim();
    if (!next) {
      setError('Titel darf nicht leer sein.');
      requestAnimationFrame(() => inputRef.current?.focus());
      return;
    }
    if (next === title) {
      applyEditing(false);
      return;
    }
    savingRef.current = true;
    setSaving(true);
    setError(null);
    try {
      const savedTitle = await onSave(next);
      setDraft(typeof savedTitle === 'string' ? savedTitle : next);
      applyEditing(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Titel konnte nicht gespeichert werden.');
      requestAnimationFrame(() => inputRef.current?.focus());
    } finally {
      savingRef.current = false;
      setSaving(false);
    }
  };

  React.useImperativeHandle(ref, () => ({
    startEditing: () => {
      skipNextBlurRef.current = false;
      setDraft(title);
      setError(null);
      applyEditing(true);
    },
  }));

  if (editing) {
    // Einzeiliges Feld wie bei Claude: kein Umbruch, kein Popover — bei langem Text
    // scrollt man nativ innerhalb des Inputs statt dass Zeilen umbrechen.
    return (
      <div className={`min-w-0 flex-1 ${className}`} onClick={(event) => event.stopPropagation()}>
        <div className="relative min-w-0">
          <input
            ref={inputRef}
            type="text"
            value={draft}
            maxLength={TITLE_MAX_LENGTH}
            aria-label="Chat-Titel bearbeiten"
            disabled={saving}
            onChange={(event) => setDraft(event.target.value)}
            onBlur={() => {
              if (skipNextBlurRef.current) {
                skipNextBlurRef.current = false;
                return;
              }
              void save();
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault();
                event.currentTarget.blur();
              } else if (event.key === 'Escape') {
                event.preventDefault();
                cancel();
              }
            }}
            className={`h-7 w-full min-w-0 rounded-md border border-app-border bg-app-surface px-2 text-app-text outline-none transition-colors focus:border-pipedrive-blue focus:ring-1 focus:ring-pipedrive-blue ${
              saving ? 'pr-7' : ''
            } ${compact ? 'text-[13px]' : 'text-sm'}`}
          />
          {saving ? (
            <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 text-slate-400">
              <LoadingSpinner size="sm" />
            </span>
          ) : null}
        </div>
        {error ? <p className="mt-1 truncate text-[10px] text-red-600">{error}</p> : null}
      </div>
    );
  }

  const titleContent = (
    // Statt Ellipsis ("…") ein weicher Fade-Out zum (dynamischen) Zeilenhintergrund —
    // per Mask statt hartkodierter Farb-Overlay, damit Hover-/Active-Zustände weiter passen.
    <span
      className={`block overflow-hidden whitespace-nowrap [-webkit-mask-image:linear-gradient(to_right,black_calc(100%_-_20px),transparent_100%)] [mask-image:linear-gradient(to_right,black_calc(100%_-_20px),transparent_100%)] ${
        // Kompakt erbt die Schriftgröße von der Zeile, damit die Liste eine einheitliche Größe hat.
        compact ? 'font-normal leading-5' : 'text-lg font-bold'
      }`}
    >
      {title}
    </span>
  );

  return (
    <div className={`flex min-w-0 items-center ${className}`}>
      {onTitleClick ? (
        <button type="button" onClick={onTitleClick} className="min-w-0 flex-1 text-left">
          {titleContent}
        </button>
      ) : (
        <div className="min-w-0 flex-1">{titleContent}</div>
      )}
    </div>
  );
});
